"use client"

import { useMemo } from "react"
import { Card } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { formatAllocationPercent } from "@/lib/format-utils"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { BlendReservePosition, BlendBackstopPosition } from "@/lib/blend/positions"
import { useCurrencyPreference } from "@/hooks/use-currency-preference"

// Token colors - consistent color scheme for each token
const TOKEN_COLORS: Record<string, string> = {
  USDC: "#2775CA",   // USDC blue
  XLM: "#E5E5E5",    // Stellar off-white
  EURC: "#0052FF",   // Euro blue
  AQUA: "#00C2FF",   // Aqua cyan
  BLND: "#8B5CF6",   // Blend purple
  CETES: "#10B981",  // Green for CETES
  USDGLO: "#F59E0B", // Yellow for USDGLO
  USTRY: "#EF4444",  // Red for USTRY
  USDT: "#26A17B",   // Tether green
  EURX: "#1E3A8A",   // Dark blue for EURx
  GBPX: "#1D4ED8",   // British blue for GBPx
  OUSD: "#141414",   // Origin black for OUSD
  PYUSD: "#0066CC",  // PayPal blue for PYUSD
  USDX: "#3B82F6",   // Blue for USDx
}

function getTokenColor(symbol: string): string {
  return TOKEN_COLORS[symbol.toUpperCase()] || "#6B7280" // Default gray
}

interface PortfolioAllocationBarProps {
  positions: BlendReservePosition[]
  backstopPositions: BlendBackstopPosition[]
  isLoading?: boolean
}

interface PoolAllocation {
  poolId: string
  poolName: string
  totalValue: number
  percentage: number
  tokens: {
    symbol: string
    value: number
    percentage: number // percentage of total portfolio
  }[]
}

export function PortfolioAllocationBar({
  positions,
  backstopPositions,
  isLoading = false,
}: PortfolioAllocationBarProps) {
  // Currency preference for multi-currency display
  const { format: formatInCurrency } = useCurrencyPreference()

  const formatUsd = (value: number) => formatInCurrency(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const { poolAllocations } = useMemo(() => {
    // Group positions by pool
    const poolMap = new Map<string, PoolAllocation>()

    // Add regular positions
    for (const position of positions) {
      if (position.supplyUsdValue <= 0) continue

      const existing = poolMap.get(position.poolId)
      if (existing) {
        existing.totalValue += position.supplyUsdValue
        existing.tokens.push({
          symbol: position.symbol,
          value: position.supplyUsdValue,
          percentage: 0, // Will calculate later
        })
      } else {
        poolMap.set(position.poolId, {
          poolId: position.poolId,
          poolName: position.poolName,
          totalValue: position.supplyUsdValue,
          percentage: 0,
          tokens: [{
            symbol: position.symbol,
            value: position.supplyUsdValue,
            percentage: 0,
          }],
        })
      }
    }

    // Add backstop positions as "LP" token within each pool
    for (const bp of backstopPositions) {
      if (bp.lpTokensUsd <= 0) continue

      const existing = poolMap.get(bp.poolId)
      if (existing) {
        existing.totalValue += bp.lpTokensUsd
        existing.tokens.push({
          symbol: "LP",
          value: bp.lpTokensUsd,
          percentage: 0,
        })
      } else {
        poolMap.set(bp.poolId, {
          poolId: bp.poolId,
          poolName: bp.poolName,
          totalValue: bp.lpTokensUsd,
          percentage: 0,
          tokens: [{
            symbol: "LP",
            value: bp.lpTokensUsd,
            percentage: 0,
          }],
        })
      }
    }

    // Calculate total
    let total = 0
    for (const pool of poolMap.values()) {
      total += pool.totalValue
    }

    // Calculate percentages
    for (const pool of poolMap.values()) {
      pool.percentage = total > 0 ? (pool.totalValue / total) * 100 : 0
      for (const token of pool.tokens) {
        token.percentage = total > 0 ? (token.value / total) * 100 : 0
      }
    }

    // Sort pools by value descending
    const allocations = Array.from(poolMap.values()).sort(
      (a, b) => b.totalValue - a.totalValue
    )

    return { poolAllocations: allocations, totalValue: total }
  }, [positions, backstopPositions])

  // Show skeleton while loading
  if (isLoading) {
    return (
      <Card className="p-4 gap-0">
        <Skeleton className="h-4 w-32 mb-2" />
        <Skeleton className="h-2 w-full rounded-sm" />
      </Card>
    )
  }

  // Don't show if empty or only a single token in a single pool
  // (allocation bar is only useful when there's diversity to show)
  const hasSingleTokenInSinglePool = poolAllocations.length === 1 && poolAllocations[0].tokens.length === 1

  if (poolAllocations.length === 0 || hasSingleTokenInSinglePool) {
    return null
  }

  // Build flat list of all token segments for the bar
  const tokenSegments: {
    poolId: string
    poolName: string
    symbol: string
    value: number
    percentage: number
    color: string
  }[] = []

  for (const pool of poolAllocations) {
    // Sort tokens within pool by value descending
    const sortedTokens = [...pool.tokens].sort((a, b) => b.value - a.value)
    for (const token of sortedTokens) {
      tokenSegments.push({
        poolId: pool.poolId,
        poolName: pool.poolName,
        symbol: token.symbol,
        value: token.value,
        percentage: token.percentage,
        color: token.symbol === "LP" ? "#8B5CF6" : getTokenColor(token.symbol),
      })
    }
  }

  // Calculate pool boundary positions (for the gray line).
  const poolBoundaries = poolAllocations.reduce<Array<{
    poolId: string
    poolName: string
    percentage: number
    start: number
    end: number
  }>>((acc, pool) => {
    const start = acc.length > 0 ? acc[acc.length - 1].end : 0
    const end = start + pool.percentage
    acc.push({
      poolId: pool.poolId,
      poolName: pool.poolName,
      percentage: pool.percentage,
      start,
      end,
    })
    return acc
  }, [])

  return (
    <Card className="p-4 gap-0">
      {/* Pool labels row */}
        <div className="relative h-5 mb-1">
          {poolBoundaries.map((pool, index) => {
            const poolData = poolAllocations.find(p => p.poolId === pool.poolId)

            if (pool.percentage >= 15) {
              return (
                <div
                  key={pool.poolId}
                  className="absolute text-xs text-muted-foreground truncate"
                  style={{
                    left: `${pool.start}%`,
                    width: `${pool.percentage}%`,
                    paddingLeft: index === 0 ? 0 : 4,
                  }}
                >
                  <span className="font-medium">{pool.poolName}</span>
                  <span className="ml-1 opacity-70">{formatAllocationPercent(pool.percentage)}</span>
                </div>
              )
            }

            // Show a small gray circle for pools with hidden labels
            return (
              <Tooltip key={pool.poolId}>
                <TooltipTrigger asChild>
                  <div
                    className="absolute flex items-end cursor-pointer h-full"
                    style={{
                      left: `${pool.start}%`,
                      width: `${pool.percentage}%`,
                      paddingLeft: index === 0 ? 0 : 4,
                      paddingBottom: 2,
                    }}
                  >
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="font-medium">{pool.poolName}</div>
                  <div className="text-zinc-400">{formatAllocationPercent(pool.percentage)} of portfolio</div>
                  {poolData && <div className="mt-1">{formatUsd(poolData.totalValue)}</div>}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>

        {/* Token allocation bar */}
        <div className="relative h-2 flex rounded-sm overflow-hidden">
          {tokenSegments.map((segment, index) => {
            // Check if this is a pool boundary (first token of a new pool)
            const isPoolBoundary = index > 0 && segment.poolId !== tokenSegments[index - 1].poolId

            return (
              <Tooltip key={`${segment.poolId}-${segment.symbol}-${index}`}>
                <TooltipTrigger asChild>
                  <div
                    className="h-full transition-opacity hover:opacity-80 cursor-pointer"
                    style={{
                      width: `${segment.percentage}%`,
                      backgroundColor: segment.color,
                      borderLeft: isPoolBoundary ? "2px solid var(--background)" : undefined,
                      minWidth: segment.percentage > 0 ? 2 : 0,
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex items-center gap-1.5 font-medium">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: segment.color }}
                    />
                    {segment.symbol}
                  </div>
                  <div className="text-zinc-400">{segment.poolName}</div>
                  <div className="mt-1">
                    {formatUsd(segment.value)} ({formatAllocationPercent(segment.percentage)})
                  </div>
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs">
          {/* Get unique tokens */}
          {Array.from(new Set(tokenSegments.map(s => s.symbol))).map(symbol => (
            <div key={symbol} className="flex items-center gap-1.5">
              <div
                className="w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: symbol === "LP" ? "#8B5CF6" : getTokenColor(symbol) }}
              />
              <span className="text-muted-foreground">{symbol}</span>
            </div>
          ))}
        </div>
    </Card>
  )
}
